import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Info, X } from "lucide-react";
import { FinalCta } from "@/components/sections/FinalCta";
import { ResourcesCatalog } from "@/components/resources/ResourcesCatalog";
import { ResourcesGridSkeleton } from "@/components/resources/ResourcesGrid";
import { ResourcesHero } from "@/components/resources/ResourcesHero";
import { useResourceAccess } from "@/hooks/useResourceAccess";
import { useSiteResources } from "@/hooks/useSiteResources";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useAppTranslation } from "@/i18n/useAppTranslation";

/** Stripe Checkout sends cancelled payments back here with ?compra=cancelada. */
function CheckoutCancelledNotice() {
  const { t } = useAppTranslation("resourcesPage");
  const [params, setParams] = useSearchParams();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (params.get("compra") !== "cancelada") return;
    setShow(true);
    const next = new URLSearchParams(params);
    next.delete("compra");
    setParams(next, { replace: true });
  }, [params, setParams]);

  if (!show) return null;
  return (
    <div
      role="status"
      className="mb-8 flex items-start gap-3 rounded-2xl border border-teal-strong/20 bg-teal-soft px-5 py-4 text-base text-ink-soft"
    >
      <Info className="mt-0.5 size-5 shrink-0 text-teal-strong" aria-hidden="true" />
      <p className="flex-1">{t("checkoutCancelled")}</p>
      <button
        type="button"
        onClick={() => setShow(false)}
        aria-label={t("closeForm")}
        className="grid size-8 place-items-center rounded-full text-ink-muted hover:bg-white"
      >
        <X className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export function ResourcesPage() {
  usePageSeo("/recursos");
  const { resources, loading } = useSiteResources();
  const { requestAccess, modal } = useResourceAccess();
  const { t } = useAppTranslation("resourcesPage");
  const { t: tNav } = useAppTranslation("nav");

  return (
    <>
      {modal}
      <main id="main-content" tabIndex={-1} className="outline-none">
        <ResourcesHero crumb={tNav("resources")} />
        <section id="catalogo" aria-label={t("catalogTitle")} className="bg-brand-bg">
          <div className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
            <CheckoutCancelledNotice />
            {loading ? (
              <ResourcesGridSkeleton label={t("loading")} count={3} />
            ) : (
              <ResourcesCatalog items={resources} onAccessRequest={requestAccess} />
            )}
          </div>
        </section>
        <FinalCta location="resources_final" />
      </main>
    </>
  );
}
